import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY_PREFIX = "coretel:onboarding:draft:";

export interface OnboardingDraft {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  preferred_name?: string;
  date_of_birth?: string;
  mobile?: string;
  email?: string;
  home_phone?: string;
  address_line1?: string;
  address_line2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  start_date?: string;
  employment_type?: string;
  primary_role_name?: string;
  subcontractor_company_id?: string;
  next_of_kin_name?: string;
  next_of_kin_phone?: string;
  next_of_kin_relationship?: string;
  blood_type?: string;
  emergency_medical_notes?: string;
  payroll_group?: string;
  lafha_eligible?: boolean;
  bank_bsb?: string;
  bank_acct_number?: string;
  super_fund_name?: string;
  super_fund_usi?: string;
  super_member_number?: string;
}

interface StoredDraft {
  payload: OnboardingDraft;
  currentStep: number;
  savedAt: string;
}

function readStored(key: string | null): { payload: OnboardingDraft; currentStep: number } {
  if (!key) return { payload: {}, currentStep: 1 };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { payload: {}, currentStep: 1 };
    const parsed = JSON.parse(raw) as Partial<StoredDraft> & OnboardingDraft;
    // New shape: { payload, currentStep, savedAt }
    if (parsed && typeof parsed === "object" && "payload" in parsed) {
      return {
        payload: (parsed.payload ?? {}) as OnboardingDraft,
        currentStep: typeof parsed.currentStep === "number" ? parsed.currentStep : 1,
      };
    }
    // Legacy shape: the stored value IS the payload (pre-3.2c.5).
    return { payload: parsed as OnboardingDraft, currentStep: 1 };
  } catch {
    return { payload: {}, currentStep: 1 };
  }
}

export function useOnboardingDraft(targetUserId: string | null) {
  const storageKey = targetUserId ? STORAGE_KEY_PREFIX + targetUserId : null;

  const [draft, setDraft] = useState<OnboardingDraft>(() => readStored(storageKey).payload);
  const [currentStep, setCurrentStep] = useState<number>(() => readStored(storageKey).currentStep);
  const keyRef = useRef(storageKey);

  useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    const fresh = readStored(storageKey);
    setDraft(fresh.payload);
    setCurrentStep(fresh.currentStep);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const stored: StoredDraft = {
        payload: draft,
        currentStep,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      // quota exceeded or private mode — silently drop
    }
  }, [draft, currentStep, storageKey]);

  const updateField = useCallback(
    <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => {
      setDraft(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const updateMany = useCallback((patch: Partial<OnboardingDraft>) => {
    setDraft(prev => ({ ...prev, ...patch }));
  }, []);

  const clear = useCallback(() => {
    if (storageKey) {
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    }
    setDraft({});
    // currentStep intentionally not reset — the submit flow relies on Step 6
    // staying mounted for ~1500ms so the success screen renders before navigate.
    // The gate makes the brief residual localStorage write unreachable.
  }, [storageKey]);

  return { draft, currentStep, setCurrentStep, updateField, updateMany, clear };
}
